import { Box, Tooltip, Typography } from "@mui/material";
import { focusRingSx, tooltipSx } from "../../overviewPanel";
import type { Tone } from "../../../polish/LabelValueRow";
import { signedPercent, signTone, sinceHeaderLine } from "./feedModel";
import type { SinceReport as SinceReportData } from "./types";

interface SinceReportProps {
  since: SinceReportData | null;
  /** The quote's currency, for the reference close and price in the tooltip. */
  priceCurrency: string;
  isDark: boolean;
  /** Beside the company on a hub card; the report page's results card ends on it. */
  align?: "left" | "right";
  /** sm: the default; md: beside a company, on a hub card or a report's header. */
  size?: "sm" | "md";
}

const VALUE_SIZES = {
  sm: { fontSize: "1.125rem", lineHeight: "24px" },
  md: {
    fontSize: { xs: "1.125rem", md: "1.3125rem" },
    lineHeight: { xs: "24px", md: "28px" },
  },
} as const;

/** Insight's own price colours (the ticker header's), not the tonal ones. */
export const insightToneColour = (tone: Tone): string => {
  if (tone === "gain") return "success.main";
  if (tone === "loss") return "error.main";
  return "text.secondary";
};

/** Whether there is a return to show: a finite figure with its reference close and price. */
export const sinceReportShown = (
  since: SinceReportData | null,
  priceCurrency: string,
): since is SinceReportData =>
  since !== null &&
  Number.isFinite(since.returnPct) &&
  sinceHeaderLine(since, priceCurrency) !== null;

/** "Since report +18.5%", its reference close and price behind a tap or hover. */
export const SinceReport = ({
  since,
  priceCurrency,
  isDark,
  align = "right",
  size = "sm",
}: SinceReportProps) => {
  if (!sinceReportShown(since, priceCurrency)) return null;
  const line = sinceHeaderLine(since, priceCurrency);
  const tone = signTone(since.returnPct);
  const labelSx = {
    fontSize: size === "md" ? { xs: "0.75rem", md: "0.8125rem" } : "0.75rem",
    lineHeight: "16px",
    fontWeight: 500,
    color: "text.secondary",
  } as const;
  return (
    <Tooltip
      title={line}
      describeChild
      placement={align === "left" ? "bottom-start" : "bottom-end"}
      enterDelay={150}
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      slotProps={{ tooltip: { sx: tooltipSx(isDark) } }}
    >
      <Box
        tabIndex={0}
        data-testid="since-report"
        sx={{
          ...focusRingSx,
          borderRadius: "8px",
          textAlign: align,
          px: 0.5,
        }}
      >
        <Typography sx={labelSx}>Since report</Typography>
        <Typography
          data-testid="since-report-value"
          sx={{
            ...VALUE_SIZES[size],
            fontWeight: 700,
            color: insightToneColour(tone),
          }}
        >
          {signedPercent(since.returnPct)}
        </Typography>
      </Box>
    </Tooltip>
  );
};
